import type {
  SignalsSeriesPayload,
  SignalsChannel,
  SignalsPass,
  SignalsQuery,
  InfluxSeriesQuery,
  InfluxMachineStatus,
  SignalMachinesList,
  SignalSourcesList,
  SignalTarget,
} from '../type/SignalsTypes';
import type { BaseResponse, HttpClient } from '@/lib/http/HttpClient';

type RawChannel = { tag: string; points: Array<[string, number | null]> };

type LastPassSignalsResponse = BaseResponse & {
  output: {
    channels: RawChannel[];
    passes: Array<{ passIndex: number; start: string; end: string; samples: number }>;
    partId: string;
    lastTime: string;
    warning: string | null;
  };
};

type SignalSourcesResponse = BaseResponse & { output: SignalSourcesList };
type SignalMachinesResponse = BaseResponse & { output: SignalMachinesList };
type InfluxSignalsResponse = BaseResponse & { output: string[] };
type InfluxMachineStatusResponse = BaseResponse & { output: { last_time: string | null } };
type InfluxSeriesResponse = BaseResponse & {
  output: { channels: RawChannel[]; aggregate_window_ms: number };
};

// t=0 is the earliest timestamp across every returned channel point — shared by the
// last-pass path and the influx series path so both charts share one time convention.
// startTime (that earliest instant, as ISO-8601) is returned alongside so charts can
// reconstruct absolute clock time for axis ticks/tooltips: startTime + t seconds.
function mapChannelsToRelativeSeconds(channels: RawChannel[]): {
  mappedChannels: SignalsChannel[];
  durationSeconds: number;
  startTime: string;
  toSeconds: (isoTs: string) => number;
} {
  let earliestMs = Infinity;
  for (const channel of channels) {
    for (const [isoTs] of channel.points) {
      const ms = new Date(isoTs).getTime();
      if (ms < earliestMs) earliestMs = ms;
    }
  }
  if (!Number.isFinite(earliestMs)) earliestMs = 0;

  const toSeconds = (isoTs: string) => (new Date(isoTs).getTime() - earliestMs) / 1000;

  const mappedChannels: SignalsChannel[] = channels.map((channel) => ({
    tag: channel.tag,
    points: channel.points.map(
      ([isoTs, value]) => [toSeconds(isoTs), value] as [number, number | null]
    ),
  }));

  const durationSeconds = mappedChannels.reduce((max, channel) => {
    const last = channel.points[channel.points.length - 1];
    return last ? Math.max(max, last[0]) : max;
  }, 0);

  return { mappedChannels, durationSeconds, startTime: new Date(earliestMs).toISOString(), toSeconds };
}

export async function signalsFetchSeries(
  query: SignalsQuery,
  http?: HttpClient
): Promise<SignalsSeriesPayload> {
  if (!http) throw new Error('ApiClient not initialized');

  const response = await http.post<LastPassSignalsResponse>('/api/signals/last-pass/read', {
    ...query.target,
    ...(query.limit !== undefined ? { limit: query.limit } : {}),
    ...(query.maxPoints !== undefined ? { maxPoints: query.maxPoints } : {}),
  });

  if (!response.ok) {
    const err = new Error(response.message ?? 'Failed to fetch signal data');
    err.name = response.name;
    throw err;
  }

  const { channels, passes, partId, lastTime, warning } = response.data.output;
  const { mappedChannels, durationSeconds, startTime, toSeconds } = mapChannelsToRelativeSeconds(channels);

  const mappedPasses: SignalsPass[] = passes.map((pass) => ({
    passIndex: pass.passIndex,
    startSeconds: toSeconds(pass.start),
    endSeconds: toSeconds(pass.end),
  }));

  return {
    durationSeconds,
    startTime,
    channels: mappedChannels,
    passes: mappedPasses,
    partId,
    lastTime,
    warning,
  };
}

export async function signalSourcesReadAll(http?: HttpClient): Promise<SignalSourcesList> {
  if (!http) throw new Error('ApiClient not initialized');

  const response = await http.post<SignalSourcesResponse>('/api/signals/sources/readAll', {});

  if (!response.ok) {
    throw new Error(response.message ?? 'Failed to fetch signal sources');
  }

  return response.data.output;
}

export async function signalMachinesReadAll(http?: HttpClient): Promise<SignalMachinesList> {
  if (!http) throw new Error('ApiClient not initialized');

  const response = await http.post<SignalMachinesResponse>('/api/signals/machines/readAll', {});

  if (!response.ok) {
    throw new Error(response.message ?? 'Failed to fetch machines');
  }

  return response.data.output;
}

export async function influxListSignals(target: SignalTarget, http?: HttpClient): Promise<string[]> {
  if (!http) throw new Error('ApiClient not initialized');

  const response = await http.post<InfluxSignalsResponse>('/api/signals/influx/signals/readAll', {
    ...target,
  });

  if (!response.ok) {
    throw new Error(response.message ?? 'Failed to fetch signals');
  }

  return response.data.output;
}

export async function influxMachineStatus(
  target: SignalTarget,
  http?: HttpClient
): Promise<InfluxMachineStatus> {
  if (!http) throw new Error('ApiClient not initialized');

  const response = await http.post<InfluxMachineStatusResponse>(
    '/api/signals/influx/machine-status/read',
    { ...target }
  );

  if (!response.ok) {
    throw new Error(response.message ?? 'Failed to fetch machine status');
  }

  return { lastTime: response.data.output.last_time };
}

export async function influxFetchSeries(
  query: InfluxSeriesQuery,
  http?: HttpClient
): Promise<SignalsSeriesPayload> {
  if (!http) throw new Error('ApiClient not initialized');

  const response = await http.post<InfluxSeriesResponse>('/api/signals/influx/series/read', {
    ...query.target,
    signals: query.signals,
    start: query.start,
    end: query.end,
    ...(query.maxPoints !== undefined ? { maxPoints: query.maxPoints } : {}),
    ...(query.toolId !== undefined && query.toolId !== '' ? { toolId: query.toolId } : {}),
  });

  if (!response.ok) {
    const err = new Error(response.message ?? 'Failed to fetch signal data');
    err.name = response.name;
    throw err;
  }

  const { channels, aggregate_window_ms } = response.data.output;
  const { mappedChannels, durationSeconds, startTime } = mapChannelsToRelativeSeconds(channels);

  return {
    durationSeconds,
    startTime,
    channels: mappedChannels,
    passes: [],
    aggregateWindowMs: aggregate_window_ms,
  };
}
